import { forwardRef, useState, useImperativeHandle, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Button } from 'antd';
import { useSelector } from 'react-redux';
import { useInterval } from 'ahooks';
import { Toast, TrackerCustomEvent } from 'common-package';
import dayjs from 'dayjs';
import { ScrollView, SlideModal } from '@/components';
import { useTracker, useWeeklyPopup } from '@/hooks';
import { selectedStoreInfo } from '@/store/device';
import { ScrollViewRefAttrs } from '../scroll-view';
import TherapyNotice from './therapy-notice';
import cx from './index.module.scss';

interface Props {}

export interface DisclaimerDialogRefAttrs {
  show: VoidFunc;
}

const DisclaimerDialog = forwardRef<DisclaimerDialogRefAttrs, Props>((props, ref) => {
  const { sendEvent } = useTracker();
  const storeInfo = useSelector(selectedStoreInfo);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const { shouldShowPopup, markAsShown } = useWeeklyPopup();
  const [count, setCount] = useState(0); // 计时器
  // 状态：是否曾经滚动到底部过
  const [hasReachedBottom, setHasReachedBottom] = useState<boolean>(false);
  // 用于记录是否已经滚动到底部过
  const bottomReachedFlag = useRef(false);

  // 计算剩余时间，当剩余时间大于0时才显示
  const remainingTime = useMemo(() => 3 - count, [count]);
  const showCountdown = useMemo(() => shouldShowPopup() && remainingTime > 0, [remainingTime, shouldShowPopup]);

  const clearLoop = useInterval(() => {
    if (showPopup) {
      setCount(count + 1);
    }
  }, 1000);

  // 检查滚动位置
  const checkScrollPosition = () => {
    if (!scrollViewRef.current?.bsInstance) return;

    const { y, maxScrollY } = scrollViewRef.current?.bsInstance;
    const threshold = 30; // 容差值

    // 检查当前是否在底部
    const currentlyAtBottom = y <= maxScrollY + threshold;

    // 如果曾经滚动到底部过，直接返回
    if (bottomReachedFlag.current) return;

    // 如果当前在底部且之前没有记录过
    if (currentlyAtBottom && !bottomReachedFlag.current) {
      bottomReachedFlag.current = true;
      setHasReachedBottom(true);
    }
  };

  const buttonEnabled = useMemo(() => {
    if (!shouldShowPopup()) return true;
    return count > 3.2 || hasReachedBottom;
  }, [count, hasReachedBottom, shouldShowPopup]);

  const clearAllValue = () => {
    setCount(0);
    setHasReachedBottom(false);
    bottomReachedFlag.current = false;
  };

  const handleAgree = useCallback(() => {
    if (shouldShowPopup()) {
      markAsShown();
      sendEvent(TrackerCustomEvent.RecordInfoDisclaimerClause, {
        et: 'CLK',
        c1: count,
        c2: dayjs()?.format('YYYY-MM-DD HH:mm:ss'),
        c3: storeInfo?.storeId,
      });
    }
    setShowPopup(false);
    clearLoop();
  }, [clearLoop, count, markAsShown, sendEvent, shouldShowPopup, storeInfo?.storeId]);

  useEffect(() => {
    if (showPopup) {
      scrollViewRef.current?.bsInstance.scrollTo(0, 0);
      const toastTimer = setTimeout(() => {
        Toast.show('请仔细阅读免责声明内容，并滑动至底部', {
          duration: 3000,
          getContainer: document.getElementById('disclaimer'),
        });
      }, 200);
      return () => {
        clearTimeout(toastTimer);
      };
    }
  }, [showPopup]);

  useEffect(() => {
    scrollViewRef.current?.handleRefresh();
    return () => {
      clearAllValue();
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      show: () => {
        setShowPopup(true);
        if (!shouldShowPopup()) clearLoop();
      },
    }),
    [clearLoop, shouldShowPopup]
  );

  return ReactDOM.createPortal(
    <SlideModal className={cx('modal-wrapper')} contentClassName={cx('scroll-content')} open={showPopup}>
      <div className={cx('modal-box')} id="disclaimer">
        <div className={cx('head')}>免责声明</div>
        <div className={cx('content')}>
          <div className={cx('scroll-wrapper')}>
            <ScrollView
              className={cx('wrapper')}
              ref={scrollViewRef}
              scrollbar={{ fade: false }}
              scrollY
              realTime
              onScrolling={pos => checkScrollPosition()}
            >
              <TherapyNotice />
            </ScrollView>
          </div>
          <div className={cx('mask')} />
        </div>
        <div className={cx('footer')}>
          <Button
            className={cx('confirm-btn', { disabled: !buttonEnabled })}
            onClick={handleAgree}
            disabled={!buttonEnabled}
          >
            <span>{buttonEnabled || !shouldShowPopup() ? '我已知晓并同意' : '阅读完成并同意'}</span>
            {showCountdown && <span className={cx('count')}>({remainingTime}s)</span>}
          </Button>
        </div>
      </div>
    </SlideModal>,
    document.body
  );
});

export default DisclaimerDialog;
